import { Controller } from "@hotwired/stimulus"
import {
  applyPlaybackToggleState,
  applySoundToggleState,
  playToggleButtonHtml,
  soundToggleButtonHtml,
  stopPreviewVideo,
  syncInitialPlaybackState,
  togglePlayback,
  toggleSound
} from "../lib/preview_video_playback.js"

// Instagram予約投稿フォームコントローラー
export default class extends Controller {
  static targets = [
    "postTypeContainer",
    "dropZone",
    "mediaPreviewContainer",
    "mediaItem",
    "fileInput",
    "fileInputMore",
    "newMediaContainer",
    "mediaFieldTemplate",
    "mediaCountLabel",
    "mediaHint",
    "postTypeLockNote",
    "positionField",
    "destroyField",
    "coverImageSection",
    "uploadArea",
    "addMoreArea",
    "captionInput",
    "captionCount",
    "hashtagCount",
    // 公開のタイミング(timing ラジオ + 主ボタン)
    "scheduleFields",
    "scheduledAtInput",
    "draftButton",
    "primaryButton",
    "primaryLabel",
    "primaryIcon",
    "primaryHint",
    "draftPostId",
    // プレビュー関連
    "previewContainer",
    "previewMedia",
    "previewCaption",
    "previewTypeBadge",
    "previewSoundNote",
    // ハッシュタグ補完関連
    "hashtagDropdown",
    "hashtagList"
  ]

  static values = {
    maxImageSize: { type: Number, default: 8 * 1024 * 1024 },      // 8MB
    maxVideoSize: { type: Number, default: 1024 * 1024 * 1024 },   // 1GB
    maxCarouselItems: { type: Number, default: 10 },
    minCarouselItems: { type: Number, default: 2 },
    maxReelsDuration: { type: Number, default: 15 * 60 },         // 15分
    maxFeedVideoDuration: { type: Number, default: 60 * 60 },     // 60分
    minVideoDuration: { type: Number, default: 3 },               // 3秒
    maxCaptionLength: { type: Number, default: 2200 },
    // 本日の残り投稿可能数(0 なら「今すぐ投稿」は押せない)
    remainingPosts: Number,
    publishConfirm: String,
    // アカウント情報
    username: String,
    profileImage: String,
    hashtagSuggestionsUrl: String,
    editUrl: String,
    updateUrl: String,
    postId: Number
  }

  connect() {
    // 既存メディアの child_index(0..n-1)と衝突しないよう大きい値から採番する
    this.newMediaIndex = 1000
    this.sortableInstance = null
    this.sortableInitialized = false
    this.previewMediaList = []
    this.currentPreviewIndex = 0
    this.hashtagSearchTimeout = null
    this.selectedHashtagIndex = -1
    this.isSelectingHashtag = false
    // 種別の切り替えを取り消したとき戻せるよう、今の選択を控えておく
    this.currentPostType = this.postType

    this.updateCaptionCount()
    this.updateMediaHint()
    this.updateTimingUI()
    this.updateButtons()
    this.updateUploadAreaVisibility()
    this.initSortable()
    this.initPreview()
  }

  disconnect() {
    if (this.sortableInstance) {
      this.sortableInstance.destroy()
      this.sortableInstance = null
    }
    if (this.hashtagSearchTimeout) {
      clearTimeout(this.hashtagSearchTimeout)
    }
    // Turbo のキャッシュ複製や画面遷移後に音が残らないよう、離脱時にプレビュー動画を止める(詳細側の disconnect と揃える)
    if (this.hasPreviewMediaTarget) stopPreviewVideo(this.previewMediaTarget.querySelector("video"))
  }

  // 選択中の投稿タイプ(image / carousel / reels)
  get postType() {
    const checked = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')
    return checked ? checked.value : "image"
  }

  // ========================================
  // プレビュー機能
  // ========================================

  initPreview() {
    // 既存メディアがあればプレビューを更新
    this.updatePreviewFromExistingMedia()
    // キャプションのプレビューを更新
    this.updateCaptionPreview()
    // 投稿タイプバッジを更新
    this.updatePreviewTypeBadge()
  }

  updatePreviewFromExistingMedia() {
    this.previewMediaList = []
    
    // 既存メディアアイテムから画像/動画URLを収集
    this.mediaItemTargets.forEach(item => {
      if (item.style.display === "none") return
      
      const img = item.querySelector("img[data-preview-media]")
      const video = item.querySelector("video[data-preview-media]")

      if (img) {
        this.previewMediaList.push({ type: "image", url: img.src })
      } else if (video) {
        // 保存済み動画は <video><source src></video>、新規追加した動画は <video src> の形
        const source = video.querySelector("source")
        const url = source?.src || video.getAttribute("src")
        if (url) {
          this.previewMediaList.push({ type: "video", url })
        }
      }
    })
    
    this.renderPreviewMedia()
  }

  renderPreviewMedia() {
    if (!this.hasPreviewMediaTarget) return
    
    const container = this.previewMediaTarget
    // 差し替える前に旧動画の音を止める。空表示・画像への切替・スライド移動・並べ替え・投稿タイプ変更・削除のどの経路でも
    // ここを通るため、音声オンのまま差し替えて音だけ残ることがない
    stopPreviewVideo(container.querySelector("video"))
    
    if (this.previewMediaList.length === 0) {
      this.currentPreviewIndex = 0
      container.innerHTML = `
        <div class="flex items-center justify-center text-gray-400">
          <div class="text-center">
            <span class="iconify lucide--image size-12"></span>
            <p class="text-xs mt-2">メディアを追加</p>
          </div>
        </div>
      `
      this.applyPreviewSoundState(null)
      return
    }
    
    // インデックスが範囲外にならないように調整
    if (this.currentPreviewIndex >= this.previewMediaList.length) {
      this.currentPreviewIndex = this.previewMediaList.length - 1
    }
    if (this.currentPreviewIndex < 0) {
      this.currentPreviewIndex = 0
    }
    
    const postType = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')?.value || "image"
    const currentMedia = this.previewMediaList[this.currentPreviewIndex]

    // メディア本体(画像/動画)。動画スライドでもカルーセルの操作UIを出すため、本体と操作UIを分けて組み立てる
    // 動画は type 属性を付けずブラウザに判定させる(video/quicktime を type に出すと Chrome が読み込みをスキップする)
    // 動画には再生/一時停止を切り替える透明ボタンを重ねる(矢印・ドットは後ろに置くので、そちらが手前になる)
    // スピーカーボタンは透明ボタンより後ろに置いて手前にする。muted 属性は Turbo のキャッシュ複製(cloneNode)でもミュートを残すため
    const mediaHtml = currentMedia.type === "video"
      ? `<div class="relative w-full flex items-center justify-center">` +
        `<video class="w-full max-h-[32rem] object-contain" src="${currentMedia.url}" autoplay muted loop playsinline ` +
        `data-action="play->scheduled-post#syncPreviewPlayback pause->scheduled-post#syncPreviewPlayback volumechange->scheduled-post#syncPreviewSound"></video>` +
        playToggleButtonHtml("click->scheduled-post#togglePreviewPlayback") +
        soundToggleButtonHtml("click->scheduled-post#togglePreviewSound") +
        `</div>`
      : `<img src="${currentMedia.url}" class="w-full max-h-[32rem] object-contain" alt="プレビュー">`

    // カルーセルの場合はナビゲーションとインジケーターを表示
    if (postType === "carousel" && this.previewMediaList.length > 1) {
      const dots = this.previewMediaList.map((_, i) =>
        `<button type="button"
                 class="w-1.5 h-1.5 rounded-full transition-colors ${i === this.currentPreviewIndex ? 'bg-blue-500' : 'bg-white/50 hover:bg-white/70'}"
                 aria-label="${i + 1}枚目を表示"
                 data-action="click->scheduled-post#goToPreviewSlide"
                 data-index="${i}"></button>`
      ).join("")

      container.innerHTML = `
        ${mediaHtml}
        <!-- 左矢印 -->
        <button type="button"
                class="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white transition-colors ${this.currentPreviewIndex === 0 ? 'opacity-0 pointer-events-none' : ''}"
                aria-label="前のメディア"
                data-action="click->scheduled-post#prevPreviewSlide">
          <span class="iconify lucide--chevron-left size-5 text-gray-700"></span>
        </button>
        <!-- 右矢印 -->
        <button type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white transition-colors ${this.currentPreviewIndex === this.previewMediaList.length - 1 ? 'opacity-0 pointer-events-none' : ''}"
                aria-label="次のメディア"
                data-action="click->scheduled-post#nextPreviewSlide">
          <span class="iconify lucide--chevron-right size-5 text-gray-700"></span>
        </button>
        <!-- ドットインジケーター -->
        <div class="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          ${dots}
        </div>
        <!-- 枚数表示 -->
        <div class="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
          ${this.currentPreviewIndex + 1}/${this.previewMediaList.length}
        </div>
      `
    } else {
      container.innerHTML = mediaHtml
    }

    // 自動再生が拒否された場合もボタン表示が実状態と合うよう、生成直後と loadeddata 時に同期する
    if (currentMedia.type === "video") {
      syncInitialPlaybackState(container.querySelector("video"), container.querySelector("[data-play-toggle]"))
    }
    // 作り直した動画は常にミュートなので、スピーカーボタンと注記もオフ表示から始める
    this.applyPreviewSoundState(currentMedia.type === "video" ? container.querySelector("video") : null)
  }

  // プレビュー動画に重ねたボタンで再生/一時停止を切り替える(ミュートは維持)
  togglePreviewPlayback(event) {
    event.preventDefault()
    const video = event.currentTarget.parentElement?.querySelector("video")
    togglePlayback(video)
  }

  // 動画の play / pause に合わせて、ボタンの aria-label と再生アイコンを切り替える
  syncPreviewPlayback(event) {
    const video = event.currentTarget
    const button = video.parentElement?.querySelector("[data-play-toggle]")
    applyPlaybackToggleState(button, video.paused)
  }

  // スピーカーボタンで元の動画の音声をオン/オフする(ボタン表示は volumechange → syncPreviewSound で追従する)。
  // オンにしたら音楽欄の「動画と合わせて試聴」を止めてもらう(同時に2つ鳴らさない)
  togglePreviewSound(event) {
    event.preventDefault()
    const video = event.currentTarget.parentElement?.querySelector("video")
    if (toggleSound(video)) this.dispatch?.("preview-sound-on")
  }

  // 動画の volumechange に合わせて、スピーカーボタンと注記の表示を video.muted から作り直す
  syncPreviewSound(event) {
    this.applyPreviewSoundState(event.currentTarget)
  }

  // ig-music:sync-preview-started / ig-music:open-picker。音楽欄の試聴が始まったとき、楽曲選択モーダル(曲ごとの試聴がある)を
  // 開いたときは、右プレビューをミュートに戻す(同時に2つ鳴らさない。再生は続けてよい)
  mutePreview() {
    if (!this.hasPreviewMediaTarget) return

    const video = this.previewMediaTarget.querySelector("video")
    if (video) video.muted = true
  }

  // ig-music:audio-changed。音声オンのまま楽曲を選ぶ/外したとき、注記の表示だけ出し直す
  // (ig-music-picker:select を直接受けると hidden の書き換えより先に動くことがあるため、ig-music の通知を待つ)
  syncPreviewSoundNote() {
    if (!this.hasPreviewMediaTarget) return

    this.applyPreviewSoundState(this.previewMediaTarget.querySelector("video"))
  }

  // video が無い(空・画像)ときはミュート扱いにして注記を隠す
  applyPreviewSoundState(video) {
    const button = video?.parentElement?.querySelector("[data-sound-toggle]") || null
    const note = this.hasPreviewSoundNoteTarget ? this.previewSoundNoteTarget : null
    applySoundToggleState(button, note, video ? video.muted : true, this.audioSelected)
  }

  // 楽曲が選択されているか(音楽欄の hidden audio_id に値があるか)。右プレビューは元の動画の音声だけを鳴らすので、その旨を注記する
  get audioSelected() {
    return Boolean(this.element.querySelector('input[name="instagram_scheduled_post[audio_id]"]')?.value)
  }

  // カルーセルプレビュー: 前のスライド
  prevPreviewSlide(event) {
    event.preventDefault()
    if (this.currentPreviewIndex > 0) {
      this.currentPreviewIndex--
      this.renderPreviewMedia()
      this.updatePreviewTypeBadge()
    }
  }

  // カルーセルプレビュー: 次のスライド
  nextPreviewSlide(event) {
    event.preventDefault()
    if (this.currentPreviewIndex < this.previewMediaList.length - 1) {
      this.currentPreviewIndex++
      this.renderPreviewMedia()
      this.updatePreviewTypeBadge()
    }
  }

  // カルーセルプレビュー: 特定のスライドへ移動
  goToPreviewSlide(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (index >= 0 && index < this.previewMediaList.length) {
      this.currentPreviewIndex = index
      this.renderPreviewMedia()
      this.updatePreviewTypeBadge()
    }
  }

  updateCaptionPreview() {
    if (!this.hasPreviewCaptionTarget || !this.hasCaptionInputTarget) return
    
    const username = this.usernameValue || "username"
    const caption = this.captionInputTarget.value || ""
    
    if (!caption.trim()) {
      this.previewCaptionTarget.innerHTML = `
        <span class="font-semibold">${username}</span>
        <span class="text-gray-500 ml-1">キャプションを入力...</span>
      `
      return
    }
    
    // ハッシュタグを青色にハイライト
    const highlightedCaption = caption
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/(#[\p{L}\p{N}\p{M}\p{So}\p{Sk}_]+)/gu, '<span class="text-blue-600">$1</span>')
      .replace(/\n/g, "<br>")
    
    this.previewCaptionTarget.innerHTML = `
      <span class="font-semibold">${username}</span>
      <span class="ml-1">${highlightedCaption}</span>
    `
  }

  updatePreviewTypeBadge() {
    if (!this.hasPreviewTypeBadgeTarget) return
    
    const postType = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')?.value || "image"
    
    // REELSバッジのみ表示（カルーセルの枚数表示はrenderPreviewMedia内で行う）
    if (postType === "reels") {
      this.previewTypeBadgeTarget.classList.remove("hidden")
      this.previewTypeBadgeTarget.querySelector("span").textContent = "REELS"
    } else {
      this.previewTypeBadgeTarget.classList.add("hidden")
    }
  }

  // ========================================
  // ハッシュタグ補完機能
  // ========================================

  handleCaptionInput(event) {
    this.updateCaptionPreview()
    
    // ハッシュタグ補完のトリガーをチェック
    const textarea = event.target
    const cursorPos = textarea.selectionStart
    const text = textarea.value.substring(0, cursorPos)
    
    // カーソル位置から直前のハッシュタグを検出
    const hashtagMatch = text.match(/#([\p{L}\p{N}\p{M}\p{So}\p{Sk}_]*)$/u)
    
    if (hashtagMatch) {
      const query = hashtagMatch[1]
      this.searchHashtags(query)
    } else {
      this.hideHashtagDropdown()
    }
  }

  handleCaptionKeydown(event) {
    if (!this.hasHashtagDropdownTarget) return
    
    const dropdown = this.hashtagDropdownTarget
    if (dropdown.classList.contains("hidden")) return
    
    const items = this.hashtagListTarget.querySelectorAll("li")
    
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedHashtagIndex = Math.min(this.selectedHashtagIndex + 1, items.length - 1)
        this.highlightHashtagItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedHashtagIndex = Math.max(this.selectedHashtagIndex - 1, 0)
        this.highlightHashtagItem(items)
        break
      case "Enter":
        if (this.selectedHashtagIndex >= 0 && items[this.selectedHashtagIndex]) {
          event.preventDefault()
          const hashtag = items[this.selectedHashtagIndex].dataset.hashtag
          this.insertHashtag(hashtag)
        }
        break
      case "Escape":
        this.hideHashtagDropdown()
        break
    }
  }

  highlightHashtagItem(items) {
    items.forEach((item, index) => {
      if (index === this.selectedHashtagIndex) {
        item.classList.add("bg-base-200")
      } else {
        item.classList.remove("bg-base-200")
      }
    })
    
    // スクロールして表示
    if (items[this.selectedHashtagIndex]) {
      items[this.selectedHashtagIndex].scrollIntoView({ block: "nearest" })
    }
  }

  async searchHashtags(query) {
    // デバウンス処理
    if (this.hashtagSearchTimeout) {
      clearTimeout(this.hashtagSearchTimeout)
    }
    
    if (!query || query.length === 0) {
      this.hideHashtagDropdown()
      return
    }
    
    this.hashtagSearchTimeout = setTimeout(async () => {
      try {
        const url = `${this.hashtagSuggestionsUrlValue}?query=${encodeURIComponent(query)}`
        const response = await fetch(url, {
          headers: {
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          }
        })
        
        if (!response.ok) throw new Error("Failed to fetch")
        
        const suggestions = await response.json()
        this.renderHashtagSuggestions(suggestions, query)
      } catch (error) {
        console.error("Hashtag search error:", error)
        this.hideHashtagDropdown()
      }
    }, 200)
  }

  renderHashtagSuggestions(suggestions, query) {
    if (!this.hasHashtagDropdownTarget || !this.hasHashtagListTarget) return
    
    if (suggestions.length === 0) {
      this.hideHashtagDropdown()
      return
    }
    
    const list = this.hashtagListTarget
    list.innerHTML = suggestions.map((s, index) => `
      <li data-hashtag="${s.text}" 
          data-action="click->scheduled-post#selectHashtag"
          class="cursor-pointer rounded-lg ${index === 0 ? 'bg-base-200' : ''}">
        <a class="flex items-center justify-between py-2">
          <span class="font-medium">#${s.text}</span>
          <span class="text-xs text-base-content/60">
            ${s.usage_count}回使用
            ${s.engagement_rate > 0 ? ` · ${s.engagement_rate}%` : ''}
          </span>
        </a>
      </li>
    `).join("")
    
    this.selectedHashtagIndex = 0
    this.hashtagDropdownTarget.classList.remove("hidden")
  }

  selectHashtag(event) {
    event.preventDefault()
    const hashtag = event.currentTarget.dataset.hashtag
    this.insertHashtag(hashtag)
  }

  insertHashtag(hashtag) {
    if (!this.hasCaptionInputTarget) return
    
    const textarea = this.captionInputTarget
    const cursorPos = textarea.selectionStart
    const text = textarea.value
    
    // カーソル位置から直前の#を含むハッシュタグ部分を特定
    const beforeCursor = text.substring(0, cursorPos)
    const afterCursor = text.substring(cursorPos)
    
    // 入力中のハッシュタグを置換
    const newBeforeCursor = beforeCursor.replace(/#[\p{L}\p{N}\p{M}\p{So}\p{Sk}_]*$/u, `#${hashtag} `)
    
    textarea.value = newBeforeCursor + afterCursor
    
    // カーソル位置を調整
    const newCursorPos = newBeforeCursor.length
    textarea.setSelectionRange(newCursorPos, newCursorPos)
    textarea.focus()
    
    this.hideHashtagDropdown()
    this.updateCaptionCount()
    this.updateCaptionPreview()
  }

  hideHashtagDropdown() {
    if (this.hasHashtagDropdownTarget) {
      this.hashtagDropdownTarget.classList.add("hidden")
    }
    this.selectedHashtagIndex = -1
  }

  // ========================================
  // Sortable初期化
  // ========================================

  async initSortable() {
    if (!this.hasMediaPreviewContainerTarget) return
    if (this.sortableInitialized) return

    try {
      // 動的インポート
      const Sortable = (await import("sortablejs")).default
      this.SortableClass = Sortable

      this.sortableInstance = Sortable.create(this.mediaPreviewContainerTarget, {
        animation: 150,
        handle: ".sort-handle",
        ghostClass: "opacity-50",
        chosenClass: "sortable-chosen",
        dragClass: "sortable-drag",
        onEnd: this.onSortEnd.bind(this)
      })

      this.sortableInitialized = true

      // 初期状態を設定
      this.updateSortableState()
    } catch (error) {
      console.error("Failed to initialize Sortable:", error)
    }
  }

  // ソート完了時
  onSortEnd(event) {
    this.updatePositions()
    this.updatePositionDisplay()
    this.updatePreviewFromExistingMedia()
  }

  // 順番表示を更新
  updatePositionDisplay() {
    let position = 1
    this.mediaItemTargets.forEach(item => {
      if (item.style.display !== "none") {
        const positionDisplay = item.querySelector('[data-position-display]')
        if (positionDisplay) {
          positionDisplay.textContent = position
        }
        position++
      }
    })
  }

  // 投稿タイプ変更
  async changePostType(event) {
    const postType = event.target.value

    // 種別ごとに使えるメディアが違う(画像1枚 / 2〜10枚 / 動画1本)ため選択済みメディアは破棄する。無確認では消さない
    if (this.getActiveMediaCount() > 0 && !confirm("選択中のメディアは破棄されます。よろしいですか？")) {
      this.restorePostType()
      return
    }

    this.clearMedia()
    this.currentPostType = postType
    this.updateMediaHint()
    this.updateMediaCountLabel()
    await this.updateSortableState()
    this.updateDragHandleVisibility()
    this.updatePreviewTypeBadge()
    this.updatePreviewFromExistingMedia()
    this.updateButtons()
    // 切替が確定した後に通知する(confirm キャンセル時は通知しない)。音楽欄(ig-music)が表示切替と楽曲の解除に使う
    this.dispatch?.("post-type-changed", { detail: { postType } })

    // リールの場合はカバー画像セクションを表示
    if (this.hasCoverImageSectionTarget) {
      this.coverImageSectionTarget.classList.toggle("hidden", postType !== "reels")
    }

    // ファイル入力のacceptを更新（両方のinputに適用）
    const updateFileInput = (input) => {
      if (!input) return
      if (postType === "reels") {
        input.accept = "video/mp4,video/quicktime"
        input.removeAttribute("multiple")
      } else if (postType === "carousel") {
        input.accept = "image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
        input.setAttribute("multiple", "")
      } else {
        input.accept = "image/jpeg,image/png,image/webp"
        input.removeAttribute("multiple")
      }
    }

    if (this.hasFileInputTarget) {
      updateFileInput(this.fileInputTarget)
    }
    if (this.hasFileInputMoreTarget) {
      updateFileInput(this.fileInputMoreTarget)
    }
  }

  // Sortableの有効/無効を更新
  async updateSortableState() {
    // Sortableがまだ初期化されていない場合は初期化を試みる
    if (!this.sortableInstance) {
      await this.initSortable()
    }
    
    if (!this.sortableInstance) return

    const postType = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')?.value || "image"
    // カルーセルの場合のみドラッグ可能
    this.sortableInstance.option("disabled", postType !== "carousel")
  }

  // ドラッグハンドルと順番表示の表示/非表示を更新
  updateDragHandleVisibility() {
    const postType = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')?.value || "image"
    const isCarousel = postType === "carousel"

    this.mediaItemTargets.forEach(item => {
      const handle = item.querySelector('.sort-handle')
      const positionDisplay = item.querySelector('[data-position-display]')?.parentElement

      if (handle) {
        handle.style.display = isCarousel ? "" : "none"
      }
      if (positionDisplay) {
        positionDisplay.style.display = isCarousel ? "" : "none"
      }
    })
  }

  // 破棄をキャンセルしたときは、選び直す前のタイプにラジオを戻す
  restorePostType() {
    const previous = this.element.querySelector(
      `input[name="instagram_scheduled_post[post_type]"][value="${this.currentPostType}"]`
    )
    if (previous) previous.checked = true
  }

  // 未保存メディア(新規追加分・422 の再描画で戻ってきた分)かどうか
  isUnsavedMedia(item) {
    return item.dataset.newMedia === "true" || item.dataset.persisted === "false"
  }

  // 選択中のメディアを全て外す(未保存分は DOM ごと、保存済みは _destroy を立てて非表示に)
  clearMedia() {
    this.mediaItemTargets.forEach(item => {
      if (this.isUnsavedMedia(item)) {
        item.remove()
        return
      }

      const destroyField = item.querySelector('[data-scheduled-post-target="destroyField"]')
      if (destroyField) {
        destroyField.value = "true"
      }
      item.style.display = "none"
    })

    if (this.hasMediaPreviewContainerTarget) {
      this.mediaPreviewContainerTarget.classList.add("hidden")
    }
    this.previewMediaList = []
    this.currentPreviewIndex = 0
    this.renderPreviewMedia()
    this.dispatch?.("video-cleared")
  }

  // 投稿タイプごとの制約は1行にまとめてドロップゾーンの下に出す
  updateMediaHint() {
    if (!this.hasMediaHintTarget) return

    const imageMb = Math.round(this.maxImageSizeValue / (1024 * 1024))
    const videoGb = Math.round(this.maxVideoSizeValue / (1024 * 1024 * 1024))
    const reelsMinutes = Math.floor(this.maxReelsDurationValue / 60)
    const hints = {
      image: `JPEG / PNG / WebP、${imageMb}MBまで、画像1枚`,
      carousel: `JPEG / PNG / WebP / MP4 / MOV、画像は${imageMb}MBまで、${this.minCarouselItemsValue}〜${this.maxCarouselItemsValue}枚`,
      reels: `MP4 / MOV、${videoGb}GBまで、${this.minVideoDurationValue}秒〜${reelsMinutes}分の動画1本`
    }
    this.mediaHintTarget.textContent = hints[this.postType] || hints.image
  }

  // ドラッグ＆ドロップ
  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  dragEnter(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-primary", "bg-primary/5")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-primary", "bg-primary/5")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-primary", "bg-primary/5")

    // FileList は live なので配列にコピーしてから非同期処理に渡す
    const files = Array.from(event.dataTransfer.files)
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  // ファイル追加
  addMedia(event) {
    // FileList は live のため、processFiles が動画の長さ検証で await している間に
    // 下の value="" で空になり、残りのファイルが無言で捨てられる。先に配列へコピーする
    const files = Array.from(event.target.files)
    // 入力をリセット（同じファイルを再選択可能に）
    event.target.value = ""
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  async processFiles(files) {
    const postType = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')?.value || "image"
    let currentCount = this.getActiveMediaCount()

    for (const file of files) {
      // 件数制限チェック
      if (postType === "image" && currentCount >= 1) {
        alert("画像投稿は1枚のみ選択できます")
        return
      }
      if (postType === "reels" && currentCount >= 1) {
        alert("リールは1つの動画のみ選択できます")
        return
      }
      if (postType === "carousel" && currentCount >= this.maxCarouselItemsValue) {
        alert(`カルーセルは最大${this.maxCarouselItemsValue}枚までです`)
        return
      }

      // ファイルタイプ・サイズチェック
      if (!this.validateFile(file, postType)) {
        continue
      }

      // 動画の場合は長さもチェック
      const isVideo = file.type.startsWith("video/")
      if (isVideo) {
        const validationResult = await this.validateVideoDuration(file, postType)
        if (!validationResult.valid) {
          alert(validationResult.message)
          continue
        }
      }

      this.addMediaPreview(file)
      currentCount++
    }

    this.updateMediaCountLabel()
    await this.updateSortableState()
  }

  validateFile(file, postType) {
    const isImage = file.type.startsWith("image/")
    const isVideo = file.type.startsWith("video/")

    // 投稿タイプに応じたファイルタイプチェック
    if (postType === "image" && !isImage) {
      alert("画像投稿には画像ファイルのみ使用できます")
      return false
    }
    if (postType === "reels" && !isVideo) {
      alert("リールには動画ファイルのみ使用できます")
      return false
    }

    // サイズチェック
    if (isImage && file.size > this.maxImageSizeValue) {
      alert(`画像は${this.maxImageSizeValue / (1024 * 1024)}MB以下にしてください: ${file.name}`)
      return false
    }
    if (isVideo && file.size > this.maxVideoSizeValue) {
      alert(`動画は${this.maxVideoSizeValue / (1024 * 1024 * 1024)}GB以下にしてください: ${file.name}`)
      return false
    }

    return true
  }

  // 動画の長さをバリデーション
  validateVideoDuration(file, postType) {
    return new Promise((resolve) => {
      const video = document.createElement("video")
      video.preload = "metadata"

      video.onloadedmetadata = () => {
        const duration = video.duration
        URL.revokeObjectURL(video.src)

        const maxDuration = postType === "reels" ? this.maxReelsDurationValue : this.maxFeedVideoDurationValue
        const minDuration = this.minVideoDurationValue

        if (duration < minDuration) {
          resolve({ valid: false, message: `動画は${minDuration}秒以上必要です: ${file.name}` })
        } else if (duration > maxDuration) {
          const maxMinutes = Math.floor(maxDuration / 60)
          resolve({ valid: false, message: `動画は${maxMinutes}分以下にしてください: ${file.name}` })
        } else {
          resolve({ valid: true, duration: Math.round(duration) })
        }
      }

      video.onerror = () => {
        URL.revokeObjectURL(video.src)
        // エラー時は通過させる（サーバー側でチェック）
        resolve({ valid: true })
      }

      video.src = URL.createObjectURL(file)
    })
  }

  addMediaPreview(file) {
    const container = this.mediaPreviewContainerTarget
    // リールのカバー場面選択(ig-cover-frame)へ動画を渡す
    if (file.type?.startsWith("video/")) this.dispatch?.("video-selected", { detail: { file } })
    // hiddenクラスを削除して表示
    container.classList.remove("hidden")
    // グリッドレイアウトを適用
    if (!container.classList.contains("grid")) {
      container.classList.add("grid", "grid-cols-2", "sm:grid-cols-4", "gap-4", "mb-4")
    }
    const index = this.newMediaIndex++
    const postType = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')?.value || "image"
    const isCarousel = postType === "carousel"

    const div = document.createElement("div")
    div.className = "relative group"
    div.dataset.scheduledPostTarget = "mediaItem"
    div.dataset.index = index
    div.dataset.newMedia = "true"
    div.dataset.persisted = "false"

    // ドラッグハンドル（カルーセルの場合のみ表示）。モバイルは hover が無いので常時表示にする
    const handleHtml = `
      <div class="sort-handle absolute top-1 left-1 cursor-grab active:cursor-grabbing bg-black/50 text-white rounded p-1 z-10 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" style="${isCarousel ? '' : 'display: none;'}">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 6h2v2H8V6zm6 0h2v2h-2V6zM8 11h2v2H8v-2zm6 0h2v2h-2v-2zm-6 5h2v2H8v-2zm6 0h2v2h-2v-2z"/>
        </svg>
      </div>
    `

    // 順番表示（カルーセルの場合のみ表示）
    const position = this.getActiveMediaCount() + 1
    const positionHtml = `
      <div class="absolute bottom-1 left-1 bg-black/50 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center" style="${isCarousel ? '' : 'display: none;'}">
        <span data-position-display>${position}</span>
      </div>
    `

    div.innerHTML = handleHtml + positionHtml

    // プレビュー生成
    const reader = new FileReader()
    reader.onload = (e) => {
      const isVideo = file.type.startsWith("video/")
      let mediaElement

      if (isVideo) {
        mediaElement = document.createElement("video")
        mediaElement.className = "w-full h-full object-contain rounded-lg"
        mediaElement.preload = "metadata"
        mediaElement.src = e.target.result
        mediaElement.dataset.previewMedia = "true"

        // 動画の長さを取得
        mediaElement.onloadedmetadata = () => {
          const duration = Math.round(mediaElement.duration)
          const durationField = div.querySelector('[data-field="duration"]')
          if (durationField) {
            durationField.value = duration
          }
        }
      } else {
        mediaElement = document.createElement("img")
        mediaElement.className = "w-full h-full object-contain rounded-lg"
        mediaElement.src = e.target.result
        mediaElement.alt = "プレビュー"
        mediaElement.dataset.previewMedia = "true"
      }

      const mediaWrapper = document.createElement("div")
      mediaWrapper.className = "w-full h-32 bg-base-200 rounded-lg overflow-hidden flex items-center justify-center"
      mediaWrapper.appendChild(mediaElement)

      div.insertBefore(mediaWrapper, div.firstChild)

      // Instagramプレビューを更新。DOM順(=表示順)からリストを組み直すので、複数ファイルの読み込み完了順に依存しない
      this.updatePreviewFromExistingMedia()
      this.updatePreviewTypeBadge()
    }
    reader.readAsDataURL(file)

    // 削除ボタン
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "absolute top-1 right-1 btn btn-circle btn-sm sm:btn-xs btn-error sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
    removeBtn.setAttribute("aria-label", "メディアを削除")
    removeBtn.dataset.action = "click->scheduled-post#removeMedia"
    removeBtn.dataset.index = index
    removeBtn.innerHTML = '<span class="iconify lucide--x size-3"></span>'
    div.appendChild(removeBtn)

    // hidden fields
    const mediaType = file.type.startsWith("video/") ? "video" : "image"

    const fieldsHtml = `
      <input type="hidden" name="instagram_scheduled_post[instagram_scheduled_post_media_attributes][${index}][media_type]" value="${mediaType}" data-field="media_type">
      <input type="hidden" name="instagram_scheduled_post[instagram_scheduled_post_media_attributes][${index}][position]" value="${position - 1}" data-field="position" data-scheduled-post-target="positionField">
      <input type="hidden" name="instagram_scheduled_post[instagram_scheduled_post_media_attributes][${index}][duration]" value="" data-field="duration">
    `
    div.insertAdjacentHTML("beforeend", fieldsHtml)

    // ファイル入力（実際のファイルを添付）
    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.name = `instagram_scheduled_post[instagram_scheduled_post_media_attributes][${index}][file]`
    fileInput.className = "hidden"
    fileInput.dataset.field = "file"
    const templateFileInput = this.mediaFieldTemplateTarget.content.querySelector('[data-field="file"]')
    fileInput.dataset.directUploadUrl = templateFileInput.dataset.directUploadUrl

    // DataTransferを使用してファイルを設定
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    fileInput.files = dataTransfer.files

    div.appendChild(fileInput)

    container.appendChild(div)
    this.updateMediaCountLabel()
  }

  // メディア削除
  removeMedia(event) {
    event.preventDefault()
    const index = event.currentTarget.dataset.index
    const item = this.mediaItemTargets.find(el => el.dataset.index === index)

    if (item) {
      if (this.isUnsavedMedia(item)) {
        // 未保存分は完全に削除
        item.remove()
      } else {
        // 保存済み分は_destroyフラグを立てて非表示に
        const destroyField = item.querySelector('[data-scheduled-post-target="destroyField"]')
        if (destroyField) {
          destroyField.value = "true"
        }
        item.style.display = "none"
      }
    }

    this.updateMediaCountLabel()
    this.updatePositions()
    this.updatePositionDisplay()
    this.updatePreviewFromExistingMedia()
    this.updatePreviewTypeBadge()

    // メディアが0件になったらコンテナを非表示に
    if (this.getActiveMediaCount() === 0) {
      this.mediaPreviewContainerTarget.classList.add("hidden")
      this.previewMediaList = []
      this.renderPreviewMedia()
      this.dispatch?.("video-cleared")
    }
  }

  // ポジション更新
  updatePositions() {
    let position = 0
    this.mediaItemTargets.forEach(item => {
      if (item.style.display !== "none") {
        const positionField = item.querySelector('[data-scheduled-post-target="positionField"]') ||
                              item.querySelector('[data-field="position"]')
        if (positionField) {
          positionField.value = position++
        }
      }
    })
  }

  // メディア数ラベル更新
  updateMediaCountLabel() {
    if (this.hasMediaCountLabelTarget) {
      const count = this.getActiveMediaCount()
      // リールは動画1本なので助数詞を変える
      const unit = this.postType === "reels" ? "本" : "枚"
      this.mediaCountLabelTarget.textContent = `${count}${unit}選択中`
    }
    this.updateUploadAreaVisibility()
    // 枚数で送信ボタンの活性が変わるため、ラベル更新と同時に見直す
    this.updateButtons()
  }

  // アップロードエリアの表示/非表示を更新
  updateUploadAreaVisibility() {
    if (!this.hasUploadAreaTarget) return

    const postType = this.element.querySelector('input[name="instagram_scheduled_post[post_type]"]:checked')?.value || "image"
    const count = this.getActiveMediaCount()

    // 大きな文言エリアを非表示にするか
    let shouldHideMainArea = false
    // 追加ボタンを表示するか
    let shouldShowAddMore = false

    if (postType === "image") {
      // 画像: 1枚あれば両方非表示
      shouldHideMainArea = count >= 1
      shouldShowAddMore = false
    } else if (postType === "reels") {
      // リール: 1つあれば両方非表示
      shouldHideMainArea = count >= 1
      shouldShowAddMore = false
    } else if (postType === "carousel") {
      // カルーセル: 1枚以上あれば大きな文言非表示、上限未満なら追加ボタン表示
      shouldHideMainArea = count >= 1
      shouldShowAddMore = count >= 1 && count < this.maxCarouselItemsValue
    }

    // 大きな文言エリアの表示/非表示
    if (shouldHideMainArea) {
      this.uploadAreaTarget.classList.add("hidden")
    } else {
      this.uploadAreaTarget.classList.remove("hidden")
    }

    // 追加ボタンの表示/非表示
    if (this.hasAddMoreAreaTarget) {
      if (shouldShowAddMore) {
        this.addMoreAreaTarget.classList.remove("hidden")
      } else {
        this.addMoreAreaTarget.classList.add("hidden")
      }
    }
  }

  // アクティブなメディア数を取得
  getActiveMediaCount() {
    return this.mediaItemTargets.filter(item => {
      if (item.style.display === "none") return false
      const destroyField = item.querySelector('[data-scheduled-post-target="destroyField"]')
      return !destroyField || destroyField.value !== "true"
    }).length
  }

  // キャプション文字数更新
  updateCaptionCount() {
    if (!this.hasCaptionInputTarget) return

    const text = this.captionInputTarget.value || ""

    if (this.hasCaptionCountTarget) {
      this.captionCountTarget.textContent = text.length
    }

    if (this.hasHashtagCountTarget) {
      // Unicode対応ハッシュタグカウント
      const hashtagCount = (text.match(/#[\p{L}\p{N}\p{M}\p{So}\p{Sk}_]+/gu) || []).length
      this.hashtagCountTarget.textContent = hashtagCount

      // 30個超えたら警告色
      if (hashtagCount > 30) {
        this.hashtagCountTarget.classList.add("text-error")
      } else {
        this.hashtagCountTarget.classList.remove("text-error")
      }
    }

    if (this.hasCaptionCountTarget) {
      this.captionCountTarget.classList.toggle("text-error", this.captionLength() > this.maxCaptionLengthValue)
    }
    this.updateButtons()
  }

  captionLength() {
    if (!this.hasCaptionInputTarget) return 0

    // Ruby の String#length と同じくコードポイント単位で数える
    return [ ...this.captionInputTarget.value ].length
  }

  // ========================================
  // 公開のタイミング
  // ========================================

  // timing はフォーム専用のラジオ(モデル属性ではない)。主ボタンの commit_action を切り替えるために使う
  get isSchedule() {
    const checked = this.element.querySelector('input[name="timing"]:checked')
    return checked ? checked.value === "schedule" : false
  }

  changeTiming() {
    this.updateTimingUI()
    this.updateButtons()
  }

  updateTimingUI() {
    const isSchedule = this.isSchedule

    if (this.hasScheduleFieldsTarget) this.scheduleFieldsTarget.classList.toggle("hidden", !isSchedule)
    // 「今すぐ」のときは disabled にして送信対象から外す(隠れた予約日時をサーバに送らない)
    if (this.hasScheduledAtInputTarget) this.scheduledAtInputTarget.disabled = !isSchedule
    if (this.hasPrimaryLabelTarget) this.primaryLabelTarget.textContent = isSchedule ? "予約する" : "今すぐ投稿"
    if (this.hasPrimaryIconTarget) {
      this.primaryIconTarget.classList.toggle("lucide--calendar-clock", isSchedule)
      this.primaryIconTarget.classList.toggle("lucide--send", !isSchedule)
    }
    if (this.hasPrimaryButtonTarget) {
      this.primaryButtonTarget.value = isSchedule ? "schedule" : "publish_now"
      this.primaryButtonTarget.classList.toggle("btn-info", isSchedule)
      this.primaryButtonTarget.classList.toggle("btn-primary", !isSchedule)
      // 確認ダイアログは即時投稿のときだけ出す
      if (isSchedule) {
        this.primaryButtonTarget.removeAttribute("data-turbo-confirm")
      } else {
        this.primaryButtonTarget.setAttribute("data-turbo-confirm", this.publishConfirmValue)
      }
    }
  }

  // メディアを全て外したら投稿タイプのロックを解除する
  // (保存済みメディアがある編集画面の初期状態はロックしたまま。0枚になって初めて選び直せる)
  updatePostTypeLock() {
    if (!this.hasPostTypeContainerTarget) return
    if (this.getActiveMediaCount() !== 0) return

    this.postTypeContainerTarget.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.disabled = false
    })
    this.postTypeContainerTarget.querySelectorAll("label").forEach(label => {
      label.classList.remove("cursor-not-allowed", "opacity-60")
      label.classList.add("cursor-pointer")
    })
    if (this.hasPostTypeLockNoteTarget) this.postTypeLockNoteTarget.classList.add("hidden")
  }

  // 下書き保存・主ボタンの活性と、押せない理由の表示をまとめて更新する
  updateButtons() {
    this.updatePostTypeLock()

    const mediaReason = this.mediaReason()
    const captionOk = this.captionLength() <= this.maxCaptionLengthValue
    const isSchedule = this.isSchedule
    // 「今すぐ」のとき日時欄は disabled だが value は読めるので、判定は予約時だけ使う
    const hasSchedule = this.hasScheduledAtInputTarget && this.scheduledAtInputTarget.value !== ""

    // メディア不足・キャプション超過は下書き保存も validation で落ちるため無効化する
    if (this.hasDraftButtonTarget) this.draftButtonTarget.disabled = !(mediaReason === null && captionOk)
    if (this.hasPrimaryButtonTarget) {
      this.primaryButtonTarget.disabled = isSchedule
        ? !(mediaReason === null && captionOk && hasSchedule)
        : !(mediaReason === null && captionOk && this.remainingPostsValue > 0)
    }

    // ボタンが押せない理由をその場で伝える(活性なら出さない)
    if (this.hasPrimaryHintTarget) {
      const reason = this.disabledReason({ mediaReason, captionOk, isSchedule, hasSchedule })
      this.primaryHintTarget.textContent = reason || ""
      this.primaryHintTarget.classList.toggle("hidden", reason === null)
    }
  }

  // 投稿タイプごとの枚数制約(画像1枚 / カルーセル2〜10枚 / リール動画1本)を満たさない理由を返す
  mediaReason() {
    const count = this.getActiveMediaCount()

    if (this.postType === "carousel") {
      if (count < this.minCarouselItemsValue) return `カルーセルは${this.minCarouselItemsValue}〜${this.maxCarouselItemsValue}枚のメディアが必要です`
      if (count > this.maxCarouselItemsValue) return `カルーセルは最大${this.maxCarouselItemsValue}枚までです`
      return null
    }

    if (this.postType === "reels") {
      if (count === 0) return "動画を1つ選択すると投稿できます"
      if (count > 1) return "リールは動画1本のみです"
      return null
    }

    if (count === 0) return "画像を1枚選択すると投稿できます"
    if (count > 1) return "画像投稿は1枚のみです"
    return null
  }

  disabledReason({ mediaReason, captionOk, isSchedule, hasSchedule }) {
    if (mediaReason) return mediaReason
    if (!captionOk) return `キャプションは${this.maxCaptionLengthValue}文字以内にしてください`
    if (isSchedule && !hasSchedule) return "予約するには日時を入力してください"
    if (!isSchedule && this.remainingPostsValue <= 0) return "本日の投稿枠を使い切っています"
    return null
  }

  // フォーム送信ハンドラー
  // 主ボタンは timing に合わせて value が publish_now / schedule に差し替わるため、ここは value だけを見る
  handleSubmit(event) {
    const submitButton = event.submitter
    const action = submitButton?.value

    // 投稿アクション時のみメディアバリデーション
    if (action === "publish_now" || action === "schedule") {
      const reason = this.mediaReason()
      if (reason) {
        event.preventDefault()
        alert(reason)
        return
      }
    }

    // 予約投稿時の日時チェック
    if (action === "schedule" && this.hasScheduledAtInputTarget && !this.scheduledAtInputTarget.value) {
      event.preventDefault()
      alert("予約日時を指定してください")
      return
    }
  }

}
